/**
 * Full Box Calculator Module
 * Handles pricing calculations for full box variants
 */
class FullBoxCalculator {
  /**
   * Debounce utility function to limit how often a function can be called
   * @param {Function} func - The function to debounce
   * @param {number} wait - The time to wait in milliseconds
   * @returns {Function} - The debounced function
   */
  static debounce(func, wait = 150) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  constructor() {
    this.elements = this.initializeElements();
    this.config = this.getConfiguration();
    
    // Store original variant ID and get Landers variant ID
    this.originalVariantId = this.getOriginalVariantId();
    this.landersVariantId = this.getLandersVariantId();
    
    // Create debounced methods
    this.debouncedCheckCartForProduct = FullBoxCalculator.debounce(this.checkCartForProduct.bind(this), 500);
    
    // Cache for cart data to reduce API calls
    this.cartCache = {
      data: { totalSqft: 0, matchingItems: [], exactVariantMatch: null },
      timestamp: 0,
      maxAge: 2000 // 2 seconds max cache age
    };
    
    if (this.elements.calculator && this.elements.input) {
      this.bindEvents();
      this.initialize();
    }
  }

  /**
   * Initialize DOM elements
   */
  initializeElements() {
    return {
      calculator: document.querySelector('.fullbox-calculator'),
      input: document.getElementById('fullbox-sqft-input'),
      quantityDisplay: document.getElementById('fullbox-quantity-number'),
      boxUnit: document.getElementById('fullbox-box-unit'),
      totalPrice: document.getElementById('fullbox-total-price'),
      wasteCheckbox: document.getElementById('fullbox-waste-checkbox'),
      variantAttributes: document.querySelector('.variant_attributes'),
      addToCartBtn: document.getElementById('fullbox-add-to-cart'),
      priceDisplay: document.getElementById('variant-price__per')
    };
  }

  /**
   * Get original variant ID from the cart form
   */
  getOriginalVariantId() {
    const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    return variantIdInput ? variantIdInput.value : null;
  }

  /**
   * Get Landers variant ID from variant options
   */
  getLandersVariantId() {
    const landersVariant = document.querySelector('input[type="radio"][data-title="Landers"]');
    return landersVariant ? landersVariant.value : null;
  }

  /**
   * Get configuration from variant attributes
   */
  getConfiguration() {
    const { variantAttributes } = this.elements;
    
    if (!variantAttributes) {
      return {
        sqftPerBox: 1,
        originalPrice: 0,
        discountedPrice: 0,
        discountThreshold: 1000,
        inventoryQuantity: 0,
        available: false,
        stockStatus: 'In-Stock',
        restockNotice: '',
      };
    }

    return {
      sqftPerBox: parseFloat(variantAttributes.getAttribute('data-sqft')) || 1,
      originalPrice: parseFloat(variantAttributes.getAttribute('data-original-price')) || 0,
      discountedPrice: parseFloat(variantAttributes.getAttribute('data-discounted-price')) || 0,
      fullBoxPrice: parseFloat(variantAttributes.getAttribute('data-fullbox-price')) || 0,
      discountThreshold: 1000, // 1000 sqft threshold for discount pricing
      inventoryQuantity: parseInt(variantAttributes.getAttribute('data-inventory-quantity')) || 0,
      available: variantAttributes.getAttribute('data-available') || false,
      restockNotice: variantAttributes.getAttribute('data-restock-notice') || '',
      stockStatus: variantAttributes.getAttribute('data-stock-status') || '',
    };
  }

  // Update stock notice based on variant metafields  
  updateStockNotice() {
    const { stockStatus: stockStatusAttr, restockNotice, available } = this.config;

    const stockStatusElement = document.querySelector(".stock-status-v4");
    const deliveryInfoElement = document.querySelector(".delivery-info-v4");
    
    // Get stock status from variant attributes, default to "In-Stock"
    let stockStatusMetafield = stockStatusAttr || 'In-Stock';
    
    // Handle array format metafields like ["Re-Stock"]
    if (Array.isArray(stockStatusMetafield) && stockStatusMetafield.length > 0) {
      stockStatusMetafield = stockStatusMetafield[0];
    }
    
    if (!stockStatusMetafield || stockStatusMetafield.trim() === "") {
      stockStatusMetafield = "In-Stock";
    }
    // Default values
    let stockStatus = "In Stock";
    let deliveryInfo = "Estimated Delivery Time 5 - 7 Business Days";
    let isAvailable = "in-stock";
    let isOutOfStock = false;
    
    // Handle different stock status types
    switch (stockStatusMetafield) {
      case "In-Stock":
        stockStatus = "In Stock";
        deliveryInfo = "Estimated Delivery Time 5 - 7 Business Days";
        isAvailable = "in-stock";
        isOutOfStock = false;
        break;
      case "Out-of-Stock":
        stockStatus = "Out of Stock";
        deliveryInfo = restockNotice;
        isAvailable = "out-of-stock";
        isOutOfStock = true;
        break;
      case "Re-Stock":
        stockStatus = "Out of Stock";
        deliveryInfo = restockNotice;
        isAvailable = "re-stock";
        isOutOfStock = true;
        break;
      case "Coming":
        stockStatus = "Out of Stock";
        deliveryInfo = restockNotice;
        isAvailable = "coming";
        isOutOfStock = true;
        break;
      default:
        // Fallback to old logic for backward compatibility
        if (restockNotice && restockNotice.trim() !== "") {
          stockStatus = "Out-of-Stock";
          deliveryInfo = restockNotice;
          isAvailable = "out-of-stock";
          isOutOfStock = true;
        }
        break;
    }
    
    // Check variant availability from Shopify for button state only
    let isVariantUnavailable = available === false || available === "false";
    
    // Update stock status elements if they exist
    if (stockStatusElement) {
      stockStatusElement.textContent = stockStatus;
      stockStatusElement.className = `stock-status-v4 ${isAvailable}`;
    }
    
    if (deliveryInfoElement) {
      deliveryInfoElement.textContent = deliveryInfo;
    }
    
    // Update add to cart button state
    let buttonShouldBeDisabled = isOutOfStock || isVariantUnavailable;
    let buttonText = null;
    
    if (isOutOfStock) {
      // Use "SOLD OUT" for Out-of-Stock and Re-Stock, keep original text for Coming
      if (stockStatusMetafield === "Out-of-Stock") {
        buttonText = "SOLD OUT";
      } else {
        buttonText = "PRE-ORDER NOW"; // For "Coming" status
      }
    } else if (isVariantUnavailable) {
      buttonText = "SOLD OUT";
    }
    
    this.updateAddToCartButtonState(buttonShouldBeDisabled, buttonText);
  }

  // Separate function to update add to cart button state
  updateAddToCartButtonState(isOutOfStock, stockStatus = null) {
    const { addToCartBtn } = this.elements;
    if (addToCartBtn) {
      if (isOutOfStock) {
          addToCartBtn.textContent = stockStatus || "SOLD OUT";
          addToCartBtn.setAttribute("data-button-label", stockStatus);

        if (stockStatus === "PRE-ORDER NOW") {
          addToCartBtn.disabled = false;
          addToCartBtn.classList.remove("disabled");
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add("disabled");
        }
      } else {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = "ADD TO CART";
       
        addToCartBtn.classList.remove("disabled");
      }
    }
  }

  /**
   * Initialize calculator
   */
  initialize() {
    // Reset calculator to initial state (but preserve any existing checkbox state)
    const { input } = this.elements;
    if (input) input.value = '';
    
    // Set default state with proper waste calculation
    this.setDefaultState();
    
    // Initialize price display
    this.updatePriceDisplay(0);
    
    // Add keyboard support for better accessibility
    this.addKeyboardSupport();

    const addToCartBtn = document.querySelector('.fullbox-add-to-cart-btn');
    const initiallyDisabled = addToCartBtn?.disabled || addToCartBtn?.hasAttribute('disabled');
    if (initiallyDisabled) {
      this.updateAddToCartButtonState(true, addToCartBtn.textContent || "SOLD OUT");
    } 
    
    // Check cart for existing items of this product
    this.checkCartForProduct();
    this.updateStockNotice();
  }
  

  /**
   * Get cached cart info or fetch new data if cache is expired
   * @param {string} [specificVariantId] - Optional variant ID to check for exact match
   * @returns {Promise<Object>} Cart information including total sqft and matching line items
   */
  async getCachedCartInfo(specificVariantId = null) {
    const now = Date.now();
    const cacheAge = now - this.cartCache.timestamp;
    
    // Use cache if it's fresh and we're not looking for a specific variant
    if (cacheAge < this.cartCache.maxAge && !specificVariantId) {
      return this.cartCache.data;
    }
    
    // Otherwise fetch fresh data
    const cartInfo = await this.checkCartForProduct(specificVariantId);
    
    // Only update cache if we're not looking for a specific variant
    if (!specificVariantId) {
      this.cartCache.data = cartInfo;
      this.cartCache.timestamp = now;
    }
    
    return cartInfo;
  }
  
  /**
   * Check cart for existing items of the same product
   * and calculate total square footage already in cart
   * @param {string} [specificVariantId] - Optional variant ID to check for exact match
   * @returns {Object} Cart information including total sqft and matching line items
   */
  async checkCartForProduct(specificVariantId = null) {
    try {
      // Get current product ID from the calculator element
      const productId = document.querySelector('.fullbox-calculator')?.getAttribute('data-product-id');
      if (!productId) return { totalSqft: 0, matchingItems: [], exactVariantMatch: null };
      
      // Fetch current cart
      const response = await fetch('/cart.js');
      if (!response.ok) return { totalSqft: 0, matchingItems: [], exactVariantMatch: null };
      
      const cart = await response.json();
      if (!cart.items || !Array.isArray(cart.items)) return { totalSqft: 0, matchingItems: [], exactVariantMatch: null };
      
      // Find all items in cart that match this product ID
      const matchingItems = cart.items.filter(item => {
        // Extract product ID from item.handle or item.product_id
        const itemProductId = item.product_id?.toString() || '';
        return itemProductId === productId;
      });
      
      // Find exact variant match if specificVariantId is provided
      let exactVariantMatch = null;
      if (specificVariantId) {
        exactVariantMatch = cart.items.find(item => {
          return item.variant_id?.toString() === specificVariantId.toString();
        });
      }
      
      if (matchingItems.length === 0) return { totalSqft: 0, matchingItems: [], exactVariantMatch };
      
      // Calculate total square footage in cart for this product
      const { sqftPerBox } = this.config;
      let totalSqftInCart = 0;
      
      matchingItems.forEach(item => {
        // For each matching item, multiply quantity by sqft per box
        totalSqftInCart += (item.quantity || 0) * sqftPerBox;
      });
      
      return {
        totalSqft: totalSqftInCart,
        matchingItems: matchingItems,
        exactVariantMatch: exactVariantMatch
      };
    } catch (error) {
      console.warn('Error checking cart for product:', error);
      return { totalSqft: 0, matchingItems: [], exactVariantMatch: null };
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const { input, wasteCheckbox, addToCartBtn } = this.elements;
    const { stockStatus } = this.config

    if (input) {
      // Create debounced calculate function for input events
      const debouncedCalculate = FullBoxCalculator.debounce(() => this.calculate(), 150);
      
      // Real-time calculation as user types (debounced)
      input.addEventListener('input', debouncedCalculate);
      input.addEventListener('change', () => this.calculate()); // No debounce on change event
      
      // Prevent negative values
      input.addEventListener('keydown', this.preventInvalidInput.bind(this));
      input.addEventListener('paste', this.handlePaste.bind(this));
    }

    if (wasteCheckbox) {
      wasteCheckbox.addEventListener('change', () => this.calculate());
    }

    if (addToCartBtn) {
     if (stockStatus && stockStatus == 'Out-of-Stock') return;
      addToCartBtn.addEventListener('click', (event) => {
        event.preventDefault(); // 🔥 Prevent default form submit
        event.stopPropagation(); // 🛑 Optional: prevent bubbling to parent handlers
        this.handleAddToCart();
      });
    }

  }

  /**
   * Add keyboard support for accessibility
   */
  addKeyboardSupport() {
    const { wasteCheckbox } = this.elements;
    
    if (wasteCheckbox) {
      // Allow space bar to toggle checkbox
      wasteCheckbox.addEventListener('keydown', (event) => {
        if (event.key === ' ') {
          event.preventDefault();
          wasteCheckbox.checked = !wasteCheckbox.checked;
          this.calculate();
        }
      });
    }
  }

  /**
   * Prevent invalid input (negative numbers, letters, etc.)
   */
  preventInvalidInput(event) {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        // Allow: home, end, left, right, down, up
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }
    
    // Prevent: everything else that isn't a number or decimal point
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && 
        (event.keyCode < 96 || event.keyCode > 105) && 
        event.keyCode !== 190 && event.keyCode !== 110) {
      event.preventDefault();
    }
  }

  /**
   * Handle paste events to validate input
   */
  handlePaste(event) {
    event.preventDefault();
    const pasteData = (event.clipboardData || window.clipboardData).getData('text');
    const numericValue = parseFloat(pasteData);
    
    // Only allow positive numbers
    if (!isNaN(numericValue) && numericValue >= 0) {
      event.target.value = numericValue;
      this.calculate();
    }
  }

  /**
   * Update variant ID based on discount threshold
   */
  updateVariantId(showDiscount) {
    const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
    
    if (!variantIdInput) {
      return;
    }
    
    // Only perform variant switching if both variant IDs are available
    if (!this.isLandersVariantAvailable()) {
      return;
    }
    
    if (showDiscount) {
      // Switch to Landers variant for discount pricing
      if (variantIdInput.value !== this.landersVariantId) {
        variantIdInput.value = this.landersVariantId;
      }
    } else {
      // Switch back to original variant for regular pricing
      if (variantIdInput.value !== this.originalVariantId) {
        variantIdInput.value = this.originalVariantId;
      }
    }
  }

  /**
   * Main calculation function
   */
  async calculate() {
    const { input, quantityDisplay, totalPrice, wasteCheckbox } = this.elements;
    const { sqftPerBox, fullBoxPrice, originalPrice, discountedPrice, discountThreshold, inventoryQuantity } = this.config;
    
    if (!input || !quantityDisplay || !totalPrice) return;

    // Get input value
    const inputValue = parseFloat(input.value) || 0;

    // Calculate base number of boxes needed from user input (round up to nearest box)
    let boxesNeeded = Math.ceil(inputValue / sqftPerBox);
    
    // Apply waste if checkbox is checked (add 10% to total sqft and recalculate boxes)
    if (wasteCheckbox) {
      const wasteCheckboxLabel = document.querySelector('.fullbox-calculator_breakdown_items .waste');
      console.log('wasteCheckboxLabel, wasteCheckboxLabel', wasteCheckboxLabel);
      
      if (wasteCheckbox.checked) {
        console.log('wasteCheckboxLabel, wasteCheckboxLabel', true);
        const wasteAdjustedSqft = inputValue * 1.1; // Add 10% to input sqft
        boxesNeeded = Math.ceil(wasteAdjustedSqft / sqftPerBox); // Recalculate boxes from new total
        wasteCheckboxLabel.style.display = 'block';
      }  else {
        wasteCheckboxLabel.style.display = 'none';
        console.log('wasteCheckboxLabel, wasteCheckboxLabel', false);
      }
    }


    // Handle zero or empty input - only reset if user has actually interacted
    if (inputValue <= 0) {
      // If input is truly empty (not just 0), maintain default state
      if (input.value === '' || input.value === null || input.value === undefined) {
        this.setDefaultState();
        return;
      } else {
        // User entered 0, so reset to 0 state
        this.displayResults(0, 0, 0, false);
        this.updateShopifyQuantity(0);
        this.updateAddToCartButton(false);
        this.updateVariantId(false); // Use original variant for 0 state
        this.updatePriceDisplay(0); // Update price display for 0 state
        return;
      }
    }


    // Check inventory limits for clearance products
    const isLimitedInventory = inventoryQuantity > 0;
    if (isLimitedInventory && boxesNeeded > inventoryQuantity) {
      // Cap the boxes at available inventory
      boxesNeeded = inventoryQuantity;
    }
    
    // Calculate actual total square footage based on box quantity (after inventory check)
    let totalSqft = boxesNeeded * sqftPerBox;
    
    // Check for existing items in cart to determine if we'll exceed discount threshold
    let cartSqft = 0;
    let cartInfo = { totalSqft: 0, matchingItems: [], exactVariantMatch: null };
    try {
      // Use cached cart info to reduce API calls
      cartInfo = await this.getCachedCartInfo();
      cartSqft = cartInfo.totalSqft || 0;
    } catch (error) {
      console.warn('Error checking cart:', error);
    }
    
    // Calculate combined square footage (current selection + cart)
    const combinedSqft = totalSqft + cartSqft;

    // Calculate original price: data-sqft * data-original-price * boxes quantity
    const originalPriceValue = sqftPerBox * originalPrice * boxesNeeded;
    
    // Calculate discounted price: data-sqft * data-discounted-price * boxes quantity
    let discountedPriceValue = 0;
    let showDiscount = false;
    
    // Show discount if either the current selection OR combined with cart exceeds threshold
    if (totalSqft >= discountThreshold || combinedSqft >= discountThreshold) {
      // Check if Landers pricing should be disabled
      const disableLandersPrice = document.querySelector('.fullbox-calculator')?.getAttribute('data-disable-landers-price') === 'true';
      const customPrice = document.querySelector('.fullbox-calculator')?.getAttribute('data-custom-price');
      
      let discountPriceLess40;
      if (disableLandersPrice && customPrice) {
        // Use custom.price metafield for discount calculation
        discountPriceLess40 = parseFloat(customPrice);
      } else {
        // Use default $0.40 discount
        discountPriceLess40 = parseFloat(originalPrice - 0.40).toFixed(2);
      }
      
      discountedPriceValue = sqftPerBox * discountPriceLess40 * boxesNeeded;
      showDiscount = true;
      
      // If combined exceeds threshold but current selection doesn't, show a message
      // if (combinedSqft >= discountThreshold && totalSqft < discountThreshold) {
      //   this.showToast('Discount applied! Combined with your cart, you qualify for bulk pricing.', 'success');
      // }

      // 👉 Auto-click the Landers label
      const landersLabel = document.getElementById('Style-Landers');
      if (landersLabel && !disableLandersPrice) {
        landersLabel.click();
      }
    }

    // Update display and form
    this.displayResults(boxesNeeded, originalPriceValue, discountedPriceValue, showDiscount);
    this.updateShopifyQuantity(boxesNeeded);
    this.updateAddToCartButton(true);
    this.updateVariantId(showDiscount); // Switch variant ID based on discount threshold
    this.updatePriceDisplay(totalSqft); // Update price per sq ft display
    
    // Trigger custom event for external integrations
    this.triggerCalculationEvent(inputValue, boxesNeeded, showDiscount ? discountedPriceValue : originalPriceValue);
  }

  /**
   * Display calculation results
   */
  displayResults(boxes, originalPrice, discountedPrice, showDiscount) {
    const { quantityDisplay, boxUnit, totalPrice: totalPriceElement } = this.elements;
    
    if (quantityDisplay) {
      quantityDisplay.textContent = boxes;
    }
    
    if (boxUnit) {
      boxUnit.textContent = boxes === 1 ? 'box' : 'boxes';
    }
    
    if (totalPriceElement) {
      if (showDiscount) {
        // Show discounted price (left, orange) and original price (right, grey strikethrough)
        totalPriceElement.innerHTML = `
          <span class="discounted-price">$${this.formatPriceWithCommas(discountedPrice)}</span>
          <span class="original-price">$${this.formatPriceWithCommas(originalPrice)}</span>
        `;
        totalPriceElement.classList.add('discounted');
      } else {
        // Show regular original price
        totalPriceElement.textContent = `$${this.formatPriceWithCommas(originalPrice)}`;
        totalPriceElement.classList.remove('discounted');
      }
    }
  }

  /**
   * Update Shopify form quantity inputs
   */
  updateShopifyQuantity(quantity) {
    // Update main Shopify quantity input
    const shopifyQuantityInput = document.querySelector('form[action="/cart/add"] input[name="quantity"]');
    if (shopifyQuantityInput) {
      shopifyQuantityInput.value = quantity;
    }

    // Update custom quantity selector if it exists
    const customQuantityInput = document.querySelector('.custom-quantity__input');
    if (customQuantityInput) {
      customQuantityInput.value = quantity;
    }

    // Update any other quantity inputs that might exist
    const allQuantityInputs = document.querySelectorAll('input[name="quantity"]');
    allQuantityInputs.forEach(input => {
      if (input !== shopifyQuantityInput && input !== customQuantityInput) {
        input.value = quantity;
      }
    });
  }

  /**
   * Reset calculator input (preserves waste checkbox state)
   */
  resetCalculator() {
    const { input } = this.elements;
    
    if (input) input.value = '';
    
    // Don't reset waste checkbox - users might want to keep it checked
    // Don't set display values here - setDefaultState() will handle them
  }

  /**
   * Set default state with 1 box and corresponding price (respecting waste checkbox)
   */
  setDefaultState() {
    const { quantityDisplay, boxUnit, totalPrice, wasteCheckbox } = this.elements;
    const { sqftPerBox, originalPrice, discountedPrice, discountThreshold, inventoryQuantity } = this.config;
    
    // Start with 1 box worth of sqft
    let defaultBoxes = 1;
    let totalSqft = sqftPerBox * defaultBoxes;
    
    // Apply waste if checkbox is checked (add 10% to total sqft and recalculate boxes)
    if (wasteCheckbox && wasteCheckbox.checked) {
      totalSqft = totalSqft * 1.1; // Add 10% to total sqft
      defaultBoxes = Math.ceil(totalSqft / sqftPerBox); // Recalculate boxes from new total
    }
    
    // Check inventory limits for clearance products
    const isLimitedInventory = inventoryQuantity > 0;
    if (isLimitedInventory && defaultBoxes > inventoryQuantity) {
      // Cap the boxes at available inventory
      defaultBoxes = inventoryQuantity;
      // Recalculate total square footage based on available boxes
      totalSqft = defaultBoxes * sqftPerBox;
    }
    
    if (quantityDisplay) {
      quantityDisplay.textContent = defaultBoxes;
    }
    
    if (boxUnit) {
      boxUnit.textContent = defaultBoxes === 1 ? 'box' : 'boxes';
    }
    
    // Calculate original price: data-sqft * data-original-price * boxes quantity
    const originalPriceValue = sqftPerBox * originalPrice * defaultBoxes;
    
    // Calculate discounted price: data-sqft * data-discounted-price * boxes quantity
    let discountedPriceValue = 0;
    let showDiscount = false;
    if (totalSqft >= discountThreshold) {
      discountedPriceValue = sqftPerBox * discountedPrice * defaultBoxes;
      showDiscount = true;
    }

 
    if (totalPrice) {
      if (showDiscount) {


        // Show discounted price (left, orange) and original price (right, grey strikethrough)
        totalPrice.innerHTML = `
          <span class="discounted-price">$${this.formatPriceWithCommas(discountedPriceValue)}</span>
          <span class="original-price">$${this.formatPriceWithCommas(originalPriceValue)}</span>
        `;
        totalPrice.classList.add('discounted');
      } else {
        // Show regular original price
        totalPrice.textContent = `$${this.formatPriceWithCommas(originalPriceValue)}`;
        totalPrice.classList.remove('discounted');
      }
    }
    
    // Update Shopify quantity and enable add to cart
    this.updateShopifyQuantity(defaultBoxes);
    this.updateAddToCartButton(true);
    this.updateVariantId(showDiscount); // Switch variant ID based on discount threshold
    this.updatePriceDisplay(totalSqft); // Update price per sq ft display
  }

  /**
   * Show calculator
   */
  show() {
    if (this.elements.calculator) {
      this.elements.calculator.classList.add('active');
      this.elements.calculator.classList.remove('fullbox-calculator--hidden');
      this.elements.calculator.style.display = 'block';
    }
  }

  /**
   * Hide calculator
   */
  hide() {
    if (this.elements.calculator) {
      this.elements.calculator.classList.remove('active');
      this.elements.calculator.classList.add('fullbox-calculator--hidden');
      this.elements.calculator.style.display = 'none';
    }
  }

  /**
   * Update configuration (useful when variant changes)
   */
  updateConfiguration() {
    this.config = this.getConfiguration();
    
    // Refresh variant IDs in case the selected variant changed
    this.originalVariantId = this.getOriginalVariantId();
    this.landersVariantId = this.getLandersVariantId();
    
    // Get current input value for price display update
    const currentInputValue = parseFloat(this.elements.input?.value) || 0;
    
    this.calculate(); // Recalculate with new configuration
    this.updatePriceDisplay(currentInputValue); // Update price display with new configuration
  }

  /**
   * Check if Landers variant switching is available
   */
  isLandersVariantAvailable() {
    return this.landersVariantId && this.originalVariantId;
  }

  /**
   * Trigger custom event for external integrations
   */
  triggerCalculationEvent(inputValue, boxes, totalPrice) {
    const event = new CustomEvent('fullboxCalculated', {
      detail: {
        inputValue,
        boxes,
        totalPrice,
        timestamp: Date.now()
      }
    });
    
    document.dispatchEvent(event);
  }

  /**
   * Public API methods
   */
  getCalculationResults() {
    const { input, quantityDisplay, totalPrice } = this.elements;
    
    return {
      inputValue: parseFloat(input?.value) || 0,
      boxes: parseInt(quantityDisplay?.textContent) || 0,
      totalPrice: parseFloat(totalPrice?.textContent?.replace('$', '')) || 0
    };
  }

  /**
   * Set input value programmatically
   */
  setInputValue(value) {
    if (this.elements.input) {
      this.elements.input.value = value;
      this.calculate();
    }
  }

  /**
   * Set waste checkbox state
   */
  setWasteRecommendation(checked) {
    if (this.elements.wasteCheckbox) {
      this.elements.wasteCheckbox.checked = checked;
      this.calculate();
    }
  }

  /**
   * Format price with commas and proper decimal places
   */
  formatPriceWithCommas(price) {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Update add to cart button state
   */
  updateAddToCartButton(enabled) {
    const { addToCartBtn } = this.elements;
    const { stockNotice, available } = this.config;
    
    if (addToCartBtn) {

      // if (stockNotice != 'In-Stock' || !available) {
      //   addToCartBtn.disabled = true;
      //   return addToCartBtn.textContent = 'Sold Out';
      // }

      addToCartBtn.disabled = !enabled;
      if (!enabled) {
        addToCartBtn.textContent = 'Add To Cart';
      }
    }
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    const { addToCartBtn } = this.elements;
    
    if (addToCartBtn) {
      addToCartBtn.disabled = loading;
      
      if (loading) {
        addToCartBtn.innerHTML = `
          <span class="btn-spinner"></span>
          <span class="btn-text">Add To Cart</span>
        `;
        addToCartBtn.classList.add('loading');
      } else {
        addToCartBtn.innerHTML = 'Add To Cart';
        addToCartBtn.classList.remove('loading');
      }
    }
  }

  /**
   * Set success state
   */
  setSuccessState() {
    const { addToCartBtn } = this.elements;
    
    if (addToCartBtn) {
      addToCartBtn.innerHTML = `
        <span class="btn-checkmark">✓</span>
        <span class="btn-text">ADDED TO CART</span>
      `;
      addToCartBtn.classList.add('success');
      
      // Reset after 2 seconds
      setTimeout(() => {
        const buttonLabel = addToCartBtn.getAttribute('data-button-label');
        addToCartBtn.textContent = buttonLabel || "Add To Cart";
        addToCartBtn.classList.remove('loading');
        addToCartBtn.disabled = false;
      }, 2000);
    }
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'info') {
    // Remove existing toast
    const existingToast = document.querySelector('.fullbox-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `fullbox-toast fullbox-toast--${type}`;
    toast.innerHTML = `
      <div class="fullbox-toast__content">
        <span class="fullbox-toast__icon">${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
        <span class="fullbox-toast__message">${message}</span>
      </div>
      <button class="fullbox-toast__close" aria-label="Close notification">×</button>
    `;

    // Add to page
    document.body.appendChild(toast);

    // Add event listener for close button
    const closeBtn = toast.querySelector('.fullbox-toast__close');
    closeBtn.addEventListener('click', () => {
      toast.classList.add('fullbox-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    });

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.add('fullbox-toast--hiding');
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);

    // Trigger animation
    setTimeout(() => toast.classList.add('fullbox-toast--visible'), 100);
  }

  /**
   * Update cart count
   */
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });

        // Update .cart-count-bubble as well
        const cartCountBubble = document.querySelector('.cart-count-bubble');
        if (cartCountBubble) {
          cartCountBubble.textContent = cart.item_count;
          // Optionally, show/hide the bubble based on count
          if (cart.item_count > 0) {
            cartCountBubble.style.display = '';
          } else {
            cartCountBubble.style.display = 'none';
          }
        }
      })
      .catch(error => {
        console.warn('Could not update cart count:', error);
      });
  }

  /**
   * Handle add to cart button click
   */
  async handleAddToCart() {
    const { addToCartBtn } = this.elements;
    const { input, quantityDisplay } = this.elements;
    const { sqftPerBox, discountThreshold, stockNotice, available } = this.config;
    console.log('asd', !addToCartBtn ,!quantityDisplay);
    if (!addToCartBtn || !quantityDisplay) return;
    // if (stockNotice != 'In-Stock' || !available) return;

    const quantity = parseInt(quantityDisplay.textContent) || 0;

    // Only check if we have a valid quantity to add (input value is not required for default state)
    if (quantity <= 0) {
      this.showToast('Please enter a valid square footage amount', 'error');
      return;
    }

    // Calculate total square footage being added
    const addingSqft = quantity * sqftPerBox;
    
    // Check existing cart to see if we'll exceed discount threshold and if we have matching items
    let cartInfo;
    try {
      cartInfo = await this.getCachedCartInfo();
    } catch (error) {
      console.warn('Error checking cart:', error);
      cartInfo = { totalSqft: 0, matchingItems: [], exactVariantMatch: null };
    }
    
    // Calculate combined square footage (adding + already in cart)
    const combinedSqft = addingSqft + cartInfo.totalSqft;
    const willExceedThreshold = combinedSqft >= discountThreshold;
    
    // Get total boxes (current + cart)
    const existingBoxes = cartInfo.matchingItems.reduce((total, item) => total + (item.quantity || 0), 0);
    const totalBoxes = quantity + existingBoxes;
    
    this.setLoadingState(true);
    
    try {
      // Determine which variant to use based on combined square footage
      let variantId;
      let needsConsolidation = false;
      
      if (willExceedThreshold && this.landersVariantId) {
        // Use Landers variant if we'll exceed threshold and it's available
        variantId = this.landersVariantId;
        needsConsolidation = cartInfo.matchingItems.length > 0;
      } else {
        // Otherwise use current variant ID from the main product form
        const variantIdInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
        if (!variantIdInput || !variantIdInput.value) {
          this.showToast('Please select a product variant', 'error');
          this.setLoadingState(false);
          return;
        }
        variantId = variantIdInput.value;
      }
    
      // If we're switching to Landers variant and have existing items, we need to consolidate
      if (needsConsolidation && willExceedThreshold) {
        // First, remove all existing items of this product
        for (const item of cartInfo.matchingItems) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0 // Set to 0 to remove
            })
          });
        }
        
        // Then add a single consolidated item with the Landers variant
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: totalBoxes // Combined quantity
            }]
          })
        });
        
        if (response.ok) {
          this.setLoadingState(false);
          this.setSuccessState();
          this.showToast(`Cart updated: ${totalBoxes} boxes`, 'success');
          this.updateCartCount();
          
          // Invalidate the cart cache
          this.cartCache.timestamp = 0;
          
          // Quick recalculation after cart update
          setTimeout(() => this.calculate(), 100);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update cart');
        }
      } else {
        // Normal cart addition/update logic when not consolidating
        // Check if the exact same variant is already in the cart
        const exactVariantInfo = await this.getCachedCartInfo(variantId);
        const exactMatch = exactVariantInfo.exactVariantMatch;
        
        let response;
        
        if (exactMatch) {
          // If the same variant is already in the cart, update the quantity
          const lineItemKey = exactMatch.key;
          const newQuantity = exactMatch.quantity + quantity;
          
          response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: lineItemKey,
              quantity: newQuantity
            })
          });
        } else {
          // If the variant is not in the cart yet, add it as a new line item
          response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: quantity
              }]
            })
          });
        }

        if (response.ok) {
          this.setLoadingState(false);
          this.setSuccessState();
          
          // Show appropriate success message
          if (exactMatch) {
            this.showToast(`Updated cart: ${quantity} more box${quantity > 1 ? 'es' : ''} added`, 'success');
            window.location.reload(true);
          } else {
            this.showToast(`${quantity} box${quantity > 1 ? 'es' : ''} added to cart`, 'success');
          }
          
          this.updateCartCount();
          
          // Invalidate the cart cache
          this.cartCache.timestamp = 0;
          
          // Quick recalculation after cart update
          setTimeout(() => this.calculate(), 100);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add to cart');
        }
      }
    } catch (error) {
      this.showToast('Error updating cart: ' + error.message, 'error');
      this.setLoadingState(false);
    }
  }

  /**
   * Update price display based on square footage input
   */
  updatePriceDisplay(sqftValue = 0) {
    const { priceDisplay } = this.elements;
    const { originalPrice, discountedPrice, discountThreshold } = this.config;
    const pricingLabel = document.getElementById('pricing-label__per');
    
    if (!priceDisplay) return;
    
    if (sqftValue >= discountThreshold) {
      // Use discounted price for orders over 1000 sq ft
      priceDisplay.textContent = `$${parseFloat(originalPrice - 0.40).toFixed(2)}/sq ft`;
      
      // Update pricing label to display custom.price metafield when over threshold
      const customPrice = document.querySelector('.fullbox-calculator')?.getAttribute('data-custom-price');
      if (priceDisplay && customPrice) {
        priceDisplay.textContent = `$${customPrice}/sq ft`;
      }
    } else {
      // Use original price for orders 1000 sq ft or less
      priceDisplay.textContent = `$${originalPrice.toFixed(2)}/sq ft`;
      
      // Reset pricing label to default text
      if (pricingLabel) {
        pricingLabel.textContent = '(Save $.40 on orders over 1K sq ft)';
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Prevent multiple instances - only initialize if not already created
  if (!window.fullBoxCalculator) {
    // Initialize full box calculator
    window.fullBoxCalculator = new FullBoxCalculator();
    
    // Make it globally accessible for debugging and external integrations
    window.FullBoxCalculator = FullBoxCalculator;
    
  }
});

// Export for module systems (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FullBoxCalculator;
} 